import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  LabelList
} from 'recharts';
import ChartContainer from './ChartContainer';

interface WeeklyMixedComparisonChartProps {
  data: {
    dia: string;
    data: string;
    total_interacoes: number;
    total_interacoes_anterior: number;
    porcentagem?: number;
  }[];
  loading?: boolean;
}

function WeeklyMixedComparisonChart({
  data,
  loading = false,
}: WeeklyMixedComparisonChartProps) {
  // Preparar dados com ordenação correta e tratamento de dias futuros
  const preparedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    
    console.log("Dados originais recebidos:", data);
    console.log("Dias presentes nos dados originais:", data.map(item => item.dia).join(', '));
    
    const today = new Date();
    const currentDayOfWeek = today.getDay(); // 0 = Domingo, 1 = Segunda, ..., 6 = Sábado
    console.log("Dia atual da semana:", currentDayOfWeek, "- Corresponde a:", 
                ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][currentDayOfWeek]);
    
    // Mapeamento de abreviação do dia da semana para índice
    const dayMapping: Record<string, number> = {
      'Dom': 0, 
      'Seg': 1, 
      'Ter': 2, 
      'Qua': 3, 
      'Qui': 4, 
      'Sex': 5, 
      'Sáb': 6
    };
    
    // Processar os dados: zerar dias futuros e ordenar os dias
    return data
      .map(item => {
        // Se o dia é futuro (após o dia atual da semana), zeramos as interações atuais
        if (dayMapping[item.dia] > currentDayOfWeek) {
          return {
            ...item,
            total_interacoes: 0
          };
        }
        return item;
      })
      .sort((a, b) => {
        // Ordenar os dias em ordem cronológica (Dom, Seg, Ter, Qua, Qui, Sex, Sáb)
        const dayOrderA = dayMapping[a.dia] ?? 0;
        const dayOrderB = dayMapping[b.dia] ?? 0;
        return dayOrderA - dayOrderB;
      });
  }, [data]);

  // Encontrar o valor máximo de interações para ajustar a escala
  const maxInteractions = React.useMemo(() => {
    if (!preparedData || preparedData.length === 0) return 1000;
    
    // Verificar o máximo entre todos os tipos de dados (atual e semana_anterior)
    const allValues = preparedData.flatMap(item => [
      item.total_interacoes || 0,
      item.total_interacoes_anterior || 0
    ]);
    
    const max = Math.max(...allValues);
    
    // Arredondar para o próximo múltiplo apropriado
    if (max <= 100) return 100;
    if (max <= 500) return Math.ceil(max / 100) * 100;
    if (max <= 1000) return 1000;
    return Math.ceil(max / 1000) * 1000; // Para valores acima de 1000
  }, [preparedData]);

  // Calcular intervalos do eixo Y com base no valor máximo
  const calculateYAxisTicks = () => {
    if (maxInteractions <= 100) return [0, 20, 40, 60, 80, 100];
    if (maxInteractions <= 500) return [0, 100, 200, 300, 400, 500];
    if (maxInteractions <= 1000) return [0, 200, 400, 600, 800, 1000];
    return [0, maxInteractions/5, maxInteractions*2/5, maxInteractions*3/5, maxInteractions*4/5, maxInteractions];
  };

  const formatYAxis = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`;
    }
    return value.toString();
  };

  // Renderizador para os rótulos de porcentagem nas barras
  const renderPercentageLabel = (props: any) => {
    const { x, y, width, height, value, index } = props;
    
    if (!preparedData || index >= preparedData.length) return null;
    
    const item = preparedData[index];
    if (!item || item.porcentagem === undefined || item.porcentagem === null) return null;
    
    // Não mostrar rótulo se o valor de interações for zero
    if (item.total_interacoes === 0) return null;
    
    const isPositive = item.porcentagem >= 0;
    
    return (
      <text 
        x={x + width / 2} 
        y={y - 8} 
        fill={isPositive ? '#10B981' : '#EF4444'} 
        textAnchor="middle" 
        fontSize={12}
        fontWeight="bold"
      >
        {isPositive ? `+${item.porcentagem}%` : `${item.porcentagem}%`}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-800 p-3 border border-gray-700 rounded shadow-lg">
          <p className="text-sm font-medium text-gray-300">{data.dia}</p>
          {data.data && <p className="text-xs text-gray-400 mb-1">{data.data}</p>}
          
          {/* Interações desta semana */}
          <p className="text-sm flex items-center text-blue-400">
            <span className="w-3 h-3 inline-block rounded-full mr-2 bg-blue-500"></span>
            <span className="font-medium">Semana atual:</span>{' '}
            {data.total_interacoes.toLocaleString()}
          </p>
          
          {/* Interações da semana anterior */}
          <p className="text-sm flex items-center text-yellow-400">
            <span className="w-3 h-3 inline-block rounded-full mr-2 bg-yellow-500"></span>
            <span className="font-medium">Semana anterior:</span>{' '}
            {data.total_interacoes_anterior.toLocaleString()}
          </p>
          
          {/* Variação percentual */}
          {data.porcentagem !== undefined && data.total_interacoes > 0 && (
            <p className={`text-sm mt-1 font-medium ${data.porcentagem >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              <span className="font-medium">Variação:</span>{' '}
              {data.porcentagem > 0 ? '+' : ''}{data.porcentagem}% vs semana anterior
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <ChartContainer 
      title="Interações Diárias da Semana" 
      tooltip="Compara o volume de interações diárias entre a semana atual e a semana anterior."
      headerContent={
        <div className="text-xs bg-blue-500/20 text-blue-300 py-1 px-2 rounded-full">
          Dados da semana atual vs. semana anterior
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : preparedData && preparedData.length > 0 ? (
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={preparedData} margin={{ top: 20, right: 30, left: 20, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis
              dataKey="dia"
              stroke="#9CA3AF"
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              stroke="#9CA3AF" 
              tick={{ fontSize: 12 }}
              domain={[0, maxInteractions]}
              ticks={calculateYAxisTicks()}
              tickFormatter={formatYAxis}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: 5 }}
              iconType="circle"
              formatter={(value) => {
                if (value === 'total_interacoes') return 'Semana Atual';
                if (value === 'total_interacoes_anterior') return 'Semana Anterior';
                return value;
              }}
            />
            <ReferenceLine y={0} stroke="#374151" />
            
            {/* Barra para semana atual */}
            <Bar 
              dataKey="total_interacoes"
              name="total_interacoes"
              fill="#3B82F6" 
              radius={[4, 4, 0, 0]}
              barSize={30}
              animationDuration={500}
            >
              <LabelList 
                dataKey="porcentagem" 
                content={renderPercentageLabel} 
              />
            </Bar>
            
            {/* Linha para semana anterior */}
            <Line
              type="monotone"
              dataKey="total_interacoes_anterior"
              name="total_interacoes_anterior"
              stroke="#FCD34D"
              strokeWidth={2}
              dot={{ r: 4, strokeWidth: 2 }}
              activeDot={{ r: 6 }}
              animationDuration={500}
              connectNulls={true}
              isAnimationActive={true}
            />
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex justify-center items-center h-48 text-gray-400">
          Sem dados disponíveis
        </div>
      )}
    </ChartContainer>
  );
}

export default WeeklyMixedComparisonChart; 